/**
 * Flattens the design tokens into `--w-*` CSS custom properties.
 *
 * Mounted onto `:root` by `globalStyles.ts`, which is what lets the 64 SCSS
 * files read tokens as `var(--w-color-brand)` without importing anything.
 *
 * `breakpoint` is deliberately excluded: custom properties are not valid inside
 * `@media` queries, so exposing it here would only invite silently-broken CSS.
 * SCSS gets the breakpoint from the generated `_tokens.scss` instead.
 */
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SHADOW,
  SPACE,
  TRANSITION,
  Z_INDEX,
} from './tokens';

type TokenGroup = Record<string, string | number>;

/** `brandSubtle` -> `brand-subtle`, `mapOverlay` -> `map-overlay`. */
function toKebabCase(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Numeric tokens are lengths and need a unit — except the two groups that are
 * genuinely unitless, where appending `px` would produce invalid CSS.
 */
function formatValue(group: string, value: string | number): string {
  if (typeof value === 'string') {
    return value;
  }
  if (group === 'font-weight' || group === 'z-index' || value === 0) {
    return String(value);
  }
  return `${value}px`;
}

function flatten(group: string, tokens: TokenGroup): Record<string, string> {
  return Object.entries(tokens).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[`--w-${group}-${toKebabCase(key)}`] = formatValue(group, value);
    return acc;
  }, {});
}

export const CSS_VARIABLES: Record<string, string> = {
  ...flatten('color', COLOR),
  ...flatten('space', SPACE),
  ...flatten('radius', RADIUS),
  ...flatten('shadow', SHADOW),
  ...flatten('font-size', FONT_SIZE),
  ...flatten('font-weight', FONT_WEIGHT),
  ...flatten('transition', TRANSITION),
  ...flatten('z-index', Z_INDEX),
};

/** Typed accessor for use in `sx` props, e.g. `color: cssVar('--w-color-brand')`. */
export function cssVar(name: keyof typeof CSS_VARIABLES): string {
  return `var(${String(name)})`;
}
